import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Search, X, CheckCircle2, User2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Inline editor for an existing invoice. Replaces the original 500-project
 * dropdown with a search-as-you-type picker, and shows a derived
 * "Linked Client" line below it so the user always knows which client the
 * invoice will belong to after save. The legacy `client_name` snapshot input
 * is hidden — it's a denormalized field that goes stale; client identity
 * lives on `client_id` (derived from the linked project).
 */
export const EditInvoiceDialog = ({ invoice, open, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Autocomplete state for the project picker.
  const [projectQuery, setProjectQuery] = useState('');
  const [projectResults, setProjectResults] = useState([]);
  const [projectSearching, setProjectSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  // Cache of {project_id → {name, client_name, client_id, status}} for the
  // currently-selected project (so we can render the chip + linked-client
  // line even before the user types anything new).
  const [selectedProjectMeta, setSelectedProjectMeta] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!invoice) return;
    setForm({
      invoice_number: invoice.invoice_number || '',
      invoice_date: (invoice.invoice_date || '').slice(0, 10),
      // Hidden but persisted — kept so we don't accidentally null-out the
      // snapshot the print PDF reads if the user just edits something else.
      client_name: invoice.client_name || invoice.client_name_snapshot || '',
      project_id: invoice.project_id || '',
      project_name: invoice.project_name || '',
      client_id: invoice.client_id || '',
      subtotal: invoice.subtotal ?? invoice.amount ?? 0,
      tax_percent: invoice.tax_percent ?? 0,
      tax_applicable: invoice.tax_applicable ?? true,
      status: invoice.status || 'unpaid',
      payment_status: invoice.payment_status || 'pending',
      // `description` is the line-item summary shown in the printed PDF
      // (e.g. "AMC Renewal — Foo Hosting Space (Period: 16/06/2026 -
      // 15/06/2027)"). Auto-generated proformas pre-fill this; the user
      // can edit it freely.
      description: invoice.description || '',
      notes: invoice.notes || '',
    });
    // If the invoice already has a project_id, pre-load its metadata so the
    // linked-client line renders without an extra search.
    if (invoice.project_id) {
      api.get(`/projects/${invoice.project_id}`)
        .then(r => {
          const p = r.data?.data || r.data;
          if (!p) return;
          setSelectedProjectMeta({
            id: p.id,
            name: p.name,
            client_id: p.client_id || '',
            client_name: p.client_name || '',
            status: p.status || '',
          });
        })
        .catch(() => {});
    } else {
      setSelectedProjectMeta(null);
    }
    setProjectQuery('');
    setProjectResults([]);
    setShowResults(false);
  }, [invoice]);

  // Debounced project search — hits the same /projects endpoint that already
  // supports the client-name fallback we added earlier.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = projectQuery.trim();
    if (term.length < 2) {
      setProjectResults([]);
      setProjectSearching(false);
      return;
    }
    setProjectSearching(true);
    debounceRef.current = setTimeout(() => {
      api.get(`/projects?search=${encodeURIComponent(term)}&limit=20&sort_by=name&sort_order=asc`)
        .then(r => {
          if (r.data?.success) setProjectResults(r.data.data || []);
        })
        .catch(() => {})
        .finally(() => setProjectSearching(false));
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [projectQuery, open]);

  const updateField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSelectProject = (p) => {
    setSelectedProjectMeta({
      id: p.id,
      name: p.name,
      client_id: p.client_id || '',
      client_name: p.client_name || '',
      status: p.status || '',
    });
    setForm(f => ({
      ...f,
      project_id: p.id,
      project_name: p.name,
      client_id: p.client_id || f.client_id,
      // Also refresh the snapshot text so it doesn't drift from the new
      // linked client.
      client_name: p.client_name || f.client_name,
    }));
    setProjectQuery('');
    setProjectResults([]);
    setShowResults(false);
  };

  const handleClearProject = () => {
    setSelectedProjectMeta(null);
    setForm(f => ({ ...f, project_id: '', project_name: '', client_id: '' }));
  };

  // Local preview of tax + total so the user sees the impact of their edit
  // before saving. Server recomputes from the same formula on save.
  const subtotal = parseFloat(form.subtotal) || 0;
  const rate = parseFloat(form.tax_percent) || 0;
  const tax = form.tax_applicable && rate > 0 ? +(subtotal * rate / 100).toFixed(2) : 0;
  const total = +(subtotal + tax).toFixed(2);

  const derivedClientName = useMemo(() => {
    if (selectedProjectMeta?.client_name) return selectedProjectMeta.client_name;
    return form.client_name || '';
  }, [selectedProjectMeta, form.client_name]);

  const handleSave = async () => {
    if (!invoice) return;
    setSaving(true);
    try {
      const payment_received = form.payment_status === 'paid';
      const status = payment_received ? 'paid' : form.status;
      const payload = {
        ...form,
        subtotal: subtotal,
        sub_total: subtotal,
        tax_amount: tax,
        gst_amount: tax,
        total: total,
        total_amount: total,
        grand_total: total,
        payment_received,
        status,
        // Keep the snapshot in sync with whatever's derived (covers the case
        // where the user only changed the project and never touched the
        // hidden snapshot field).
        client_name: derivedClientName || form.client_name,
        client_name_snapshot: derivedClientName || form.client_name,
      };
      if (selectedProjectMeta) {
        payload.project_name = selectedProjectMeta.name;
        if (selectedProjectMeta.client_id) {
          payload.client_id = selectedProjectMeta.client_id;
        }
      }
      const res = await api.put(`/invoices/${invoice.id}`, payload);
      if (res.data.success) {
        toast.success('Invoice updated');
        onSaved && onSaved();
        onClose();
      } else {
        toast.error(res.data.message || 'Update failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="edit-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Edit Invoice #{form.invoice_number}</DialogTitle>
          <DialogDescription>
            Fix metadata, financials, or status. Changes are saved immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-1">
            <Label>Invoice Number</Label>
            <Input
              value={form.invoice_number || ''}
              onChange={e => updateField('invoice_number', e.target.value)}
              data-testid="edit-invoice-number"
            />
          </div>
          <div className="space-y-1">
            <Label>Invoice Date</Label>
            <Input
              type="date"
              value={form.invoice_date || ''}
              onChange={e => updateField('invoice_date', e.target.value)}
              data-testid="edit-invoice-date"
            />
          </div>

          {/* Project picker — search-as-you-type. Shows the currently
              linked project as a removable chip; typing opens a result
              list with project name · client · status. */}
          <div className="space-y-1 col-span-2 relative">
            <Label>Project</Label>
            {selectedProjectMeta && (
              <div
                className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 flex items-center gap-3"
                data-testid="edit-invoice-project-chip"
              >
                <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{selectedProjectMeta.name}</p>
                  {selectedProjectMeta.client_name && (
                    <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                      <User2 className="h-3 w-3" />
                      Linked client: <span className="font-medium text-foreground">{selectedProjectMeta.client_name}</span>
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearProject}
                  data-testid="edit-invoice-clear-project"
                  className="h-7 px-2"
                >
                  <X className="h-3.5 w-3.5 mr-1" />Change
                </Button>
              </div>
            )}
            {!selectedProjectMeta && (
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  className="pl-9"
                  placeholder="Type at least 2 characters — searches project + client names"
                  value={projectQuery}
                  onChange={e => { setProjectQuery(e.target.value); setShowResults(true); }}
                  onFocus={() => setShowResults(true)}
                  data-testid="edit-invoice-project-search"
                />
              </div>
            )}
            {!selectedProjectMeta && showResults && projectQuery.trim().length >= 2 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-[260px] overflow-y-auto">
                {projectSearching && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
                )}
                {!projectSearching && projectResults.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">No projects matched.</div>
                )}
                {projectResults.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleSelectProject(p)}
                    className="w-full text-left px-3 py-2 hover:bg-muted/40 transition-colors border-b border-border/30 last:border-0"
                    data-testid={`edit-invoice-project-option-${p.id}`}
                  >
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                      {p.client_name && <span className="truncate"><User2 className="inline h-3 w-3 mr-0.5" />{p.client_name}</span>}
                      {p.status && <span className="px-1.5 py-0 rounded bg-muted/60">{p.status}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label>Subtotal (₹)</Label>
            <Input
              type="number" step="0.01"
              value={form.subtotal ?? 0}
              onChange={e => updateField('subtotal', e.target.value)}
              data-testid="edit-invoice-subtotal"
            />
          </div>
          <div className="space-y-1">
            <Label>GST Rate (%)</Label>
            <Input
              type="number" step="0.01"
              value={form.tax_percent ?? 0}
              onChange={e => updateField('tax_percent', e.target.value)}
              data-testid="edit-invoice-tax-percent"
            />
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <Switch
              checked={!!form.tax_applicable}
              onCheckedChange={(v) => updateField('tax_applicable', v)}
              data-testid="edit-invoice-tax-applicable"
            />
            <Label>Tax Applicable</Label>
            <div className="ml-auto text-sm">
              GST: <span className="font-mono">₹{tax.toLocaleString('en-IN')}</span> &nbsp;|&nbsp;
              Total: <span className="font-mono font-semibold">₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <Select
              value={form.status}
              onValueChange={(v) => updateField('status', v)}
            >
              <SelectTrigger data-testid="edit-invoice-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Payment Status</Label>
            <Select
              value={form.payment_status}
              onValueChange={(v) => updateField('payment_status', v)}
            >
              <SelectTrigger data-testid="edit-invoice-payment-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 col-span-2">
            <Label>Description <span className="text-xs text-muted-foreground">(appears as the line-item description on the printed invoice)</span></Label>
            <Textarea
              rows={2}
              value={form.description || ''}
              onChange={e => updateField('description', e.target.value)}
              placeholder="e.g. AMC Renewal — Acme Hosting (Period: 01/04/2026 – 31/03/2027)"
              data-testid="edit-invoice-description"
            />
          </div>
          <div className="space-y-1 col-span-2">
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={form.notes || ''}
              onChange={e => updateField('notes', e.target.value)}
              data-testid="edit-invoice-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="edit-invoice-save">
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
